// 数据服务层 - 统一管理真实接口调用
// 被所有业务组件调用以获取数据

import apiClient from "./apiClient";
import config from "../config";
import { logger } from "../utils/logger";

// 创建一个空的响应体作为错误回退
const createEmptyResponse = (fallbackData: unknown = null) => {
  return {
    data: {
      code: 500,
      message: "接口请求失败",
      data: fallbackData,
    },
  };
};

// 已移除getAuthHeaders函数，认证头由apiClient拦截器统一处理

// 统一数据服务类
export class DataService {
  // 获取仪表盘统计数据
  static async getDashboardStats() {
    try {
      logger.info("📊 [真实接口] 获取仪表盘统计");
      const data = await apiClient.get(config.apiUrls.DASHBOARD_STATS);
      return data;
    } catch (error) {
      logger.error("获取仪表盘统计失败:", error);
      return { total_events: 0, abnormal_events: 0 };
    }
  }

  // 取消OTA设备升级
  static async cancelOTADeviceUpgrade(deviceId: string) {
    try {
      logger.info("⏹ [真实接口] 取消OTA设备升级", deviceId);
      const data = await apiClient.post(
        `${config.apiUrls.OTA}/device/${deviceId}/cancel`,
        {}
      );
      return data;
    } catch (error) {
      logger.error("取消OTA设备升级失败:", error);
      return createEmptyResponse();
    }
  }

  // 获取仪表板事件列表
  static async getDashboardEventLists() {
    try {
      logger.info("📋 [真实接口] 获取仪表板事件列表");
      const data = await apiClient.get(config.apiUrls.DASHBOARD_EVENTS);
      return data;
    } catch (error) {
      logger.error("获取仪表板事件列表失败:", error);
      return { waiting_events: [], ongoing_events: [], completed_events: [] };
    }
  }

  // 获取设备概览数据
  static async getDeviceOverview() {
    try {
      logger.info("📈 [真实接口] 获取设备概览");
      const data = await apiClient.get(`${config.apiUrls.DEVICES}/overview`);
      return data;
    } catch (error) {
      logger.error("获取设备概览失败:", error);
      return { overview: {}, percentages: {}, total_devices: 0 };
    }
  }

  // 获取热点区域数据
  static async getHotspotAreas() {
    try {
      logger.info("🔥 [真实接口] 获取热点区域数据");
      const data = await apiClient.get(config.apiUrls.DASHBOARD_HOTSPOTS);
      return data;
    } catch (error) {
      logger.error("获取热点区域数据失败:", error);
      return [];
    }
  }

  // 获取设备列表
  static async getDevices(params: Record<string, unknown> = {}) {
    try {
      logger.info("📋 [真实接口] 获取设备列表", params);
      const data = await apiClient.get(config.apiUrls.DEVICES, { params });
      return data;
    } catch (error) {
      logger.error("获取设备列表失败:", error);
      return { items: [], total: 0 };
    }
  }

  // 获取设备名称列表（从外部API获取）
  static async getDeviceNames(params: Record<string, unknown> = {}) {
    try {
      logger.info("📋 [真实接口] 获取设备名称列表", params);
      const data = await apiClient.post(
        `${config.apiUrls.DEVICES}/names`,
        params
      );
      return data;
    } catch (error) {
      logger.error("获取设备名称列表失败:", error);
      return { data: { code: 500, data: { total: 0, deviceNames: [] } } };
    }
  }

  // 获取单个设备详情
  static async getDeviceDetails(device_id: string) {
    try {
      logger.info("📱 [真实接口] 获取设备详情", device_id);
      const data = await apiClient.get(
        `${config.apiUrls.DEVICES}/${device_id}`
      );
      return data;
    } catch (error) {
      logger.error("获取设备详情失败:", error);
      return null;
    }
  }

  // 获取设备本月剩余通话时间
  static async getDeviceMonthlyRemaining(device_id: string) {
    try {
      logger.info("📞 [真实接口] 获取设备本月剩余通话分钟", device_id);
      const data = await apiClient.get(
        config.apiUrls.DEVICE_MONTHLY_REMAINING(device_id)
      );
      return data;
    } catch (error) {
      logger.error("获取设备剩余通话分钟失败:", error);
      return { data: { code: 500, data: null } } as any;
    }
  }

  // 清除设备WiFi配置（仅清NVS，不断网）
  static async clearDeviceWifi(device_id: string) {
    try {
      const url = `${config.apiBaseUrl}${config.apiPaths.DEVICE_CLEAR_WIFI(
        device_id
      )}`;
      const data = await apiClient.post(url, {});
      return data;
    } catch (error) {
      logger.error("清除WiFi配置失败:", error);
      return createEmptyResponse();
    }
  }

  // 强制切换4G
  static async forceDevice4G(device_id: string, persist = true) {
    try {
      const url = `${config.apiBaseUrl}${config.apiPaths.DEVICE_FORCE_4G(
        device_id
      )}`;
      const data = await apiClient.post(url, {}, { params: { persist } });
      return data;
    } catch (error) {
      logger.error("强制4G失败:", error);
      return createEmptyResponse();
    }
  }

  // 强制重启
  static async forceDeviceRestart(device_id: string) {
    try {
      const url = `${config.apiBaseUrl}${config.apiPaths.DEVICE_FORCE_RESTART(
        device_id
      )}`;
      const data = await apiClient.post(url, {});
      return data;
    } catch (error) {
      logger.error("强制重启失败:", error);
      return createEmptyResponse();
    }
  }

  // 设置设备网络模式偏好（auto 或 force_4g）
  static async setDeviceNetworkMode(
    device_id: string,
    mode: "auto" | "force_4g"
  ) {
    try {
      const url = `${config.apiBaseUrl}${config.apiPaths.DEVICE_NETWORK_MODE(
        device_id
      )}`;
      const data = await apiClient.post(url, { mode });
      return data;
    } catch (error) {
      logger.error("设置网络模式失败:", error);
      return createEmptyResponse();
    }
  }

  // 获取通话记录
  static async getCallRecords(params: Record<string, unknown> = {}) {
    try {
      logger.info("📞 [真实接口] 获取通话记录", params);

      // 转换参数名以匹配后端API期望
      const apiParams: Record<string, unknown> = {
        page: params.page || 1,
        size: params.pageSize || params.size || 20, // 后端期望 'size' 而不是 'pageSize'
        status: params.status,
        device_id: params.device_id,
        contact_name: params.contact_name,
        start_date: params.start_date,
        end_date: params.end_date,
        deviceName: params.deviceName,
        startTime: params.startTime,
        endTime: params.endTime,
        // 传递用户角色信息，由后端根据角色返回相应数据
        user_role: params.user_role,
        user_id: params.user_id,
        tenant_id: params.tenant_id,
      };

      // 移除undefined值
      Object.keys(apiParams).forEach((key) => {
        if (apiParams[key] === undefined) {
          delete apiParams[key];
        }
      });

      const data = await apiClient.get(config.apiUrls.EVENTS, {
        params: apiParams,
      });
      return data;
    } catch (error) {
      logger.error("获取通话记录失败:", error);
      return { records: [], total: 0 };
    }
  }

  // 获取事件详情（包含事件日志）
  static async getEventDetail(event_id: string) {
    try {
      logger.info("📞 [真实接口] 获取事件详情", event_id);
      const data = await apiClient.get(config.apiUrls.EVENT_BY_ID(event_id));
      return data;
    } catch (error) {
      logger.error("获取事件详情失败:", error);
      return null;
    }
  }

  // 获取通话记录统计数据
  static async getCallRecordsStats(params: Record<string, unknown> = {}) {
    try {
      logger.info("📊 [真实接口] 获取通话记录统计数据", params);

      // 转换参数名以匹配后端API期望
      const apiParams: Record<string, unknown> = {
        device_id: params.device_id,
        start_date: params.start_date,
        end_date: params.end_date,
        deviceName: params.deviceName,
        startTime: params.startTime,
        endTime: params.endTime,
        // 传递用户角色信息，由后端根据角色返回相应数据
        user_role: params.user_role,
        user_id: params.user_id,
        tenant_id: params.tenant_id,
      };

      // 移除undefined值
      Object.keys(apiParams).forEach((key) => {
        if (apiParams[key] === undefined) {
          delete apiParams[key];
        }
      });

      const data = await apiClient.get(`${config.apiUrls.EVENTS}/stats`, {
        params: apiParams,
      });
      return data;
    } catch (error) {
      logger.error("获取通话记录统计数据失败:", error);
      return {
        totalCalls: 0,
        connectedCalls: 0,
        missedCalls: 0,
        totalDuration: "0h 0m",
        totalDurationSeconds: 0,
      };
    }
  }

  // 获取月度通话时间统计数据
  static async getMonthlyCallStats(params: Record<string, unknown> = {}) {
    try {
      logger.info("📊 [真实接口] 获取月度通话时间统计数据", params);
      const requestUrl = `${config.apiUrls.EVENTS}/monthly-stats`;
      logger.info("📡 请求URL:", requestUrl);

      // 转换参数名以匹配后端API期望
      const apiParams: Record<string, unknown> = {
        deviceName: params.deviceName,
        user_role: params.user_role,
        user_id: params.user_id,
      };

      // 移除undefined值
      Object.keys(apiParams).forEach((key) => {
        if (apiParams[key] === undefined) {
          delete apiParams[key];
        }
      });

      logger.info("📡 请求参数:", apiParams);

      const data = await apiClient.get(requestUrl, {
        params: apiParams,
      });

      logger.info("📡 API响应状态:", data?.status);
      logger.info("📡 API响应数据:", data?.data);
      return data;
    } catch (error: any) {
      logger.error("获取月度通话时间统计数据失败:", {
        error: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        url: error?.config?.url,
        params: error?.config?.params,
      });
      return {
        dailyStats: [],
      };
    }
  }

  // 获取OTA固件列表
  static async getOTAFirmwareList() {
    try {
      logger.info("🔄 [真实接口] 获取OTA固件列表");
      const data = await apiClient.get(config.apiUrls.OTA_FIRMWARE);
      return data;
    } catch (error) {
      logger.error("获取OTA固件列表失败:", error);
      return [];
    }
  }

  // 获取OTA设备列表
  static async getOTADeviceList(params: Record<string, unknown> = {}) {
    try {
      logger.info("📱 [真实接口] 获取OTA设备列表", params);
      const data = await apiClient.get(config.apiUrls.OTA_DEVICES, { params });
      return data;
    } catch (error) {
      logger.error("获取OTA设备列表失败:", error);
      return { items: [], total: 0 };
    }
  }

  // 启动OTA设备升级
  static async startOTADeviceUpgrade(payload: {
    firmwareId: number;
    device_ids: string[];
  }) {
    try {
      logger.info("🚀 [真实接口] 启动OTA设备升级", payload);
      const data = await apiClient.post(
        `${config.apiUrls.OTA}/upgrade`,
        payload
      );
      return data;
    } catch (error) {
      logger.error("启动OTA设备升级失败:", error);
      return createEmptyResponse();
    }
  }

  // 上传固件文件
  static async uploadFirmware(formData: FormData) {
    try {
      logger.info("📤 [真实接口] 上传固件文件");
      const data = await apiClient.post(
        `${config.apiUrls.OTA}/firmware/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return data;
    } catch (error) {
      logger.error("上传固件文件失败:", error);
      return createEmptyResponse();
    }
  }

  // 获取音频资源列表
  static async getOTAAudioAssets() {
    try {
      logger.info("🔊 [真实接口] 获取音频资源配置");
      const data = await apiClient.get(config.apiUrls.OTA_AUDIO);
      return data;
    } catch (error) {
      logger.error("获取音频资源配置失败:", error);
      return createEmptyResponse({ alarm: null, wait: null });
    }
  }

  // 上传音频资源
  static async uploadOTAAudio(audioType: string, formData: FormData) {
    try {
      logger.info("📤 [真实接口] 上传音频资源", audioType);
      const data = await apiClient.post(
        config.apiUrls.OTA_AUDIO_UPLOAD(audioType),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return data;
    } catch (error) {
      logger.error("上传音频资源失败:", error);
      return createEmptyResponse();
    }
  }

  // 设置固件为当前版本
  static async setActiveFirmware(firmwareId: number) {
    try {
      logger.info("✅ [真实接口] 设置固件为当前版本", firmwareId);
      const data = await apiClient.put(
        `${config.apiUrls.OTA}/firmware/${firmwareId}/activate`
      );
      return data;
    } catch (error) {
      logger.error("设置固件为当前版本失败:", error);
      return createEmptyResponse();
    }
  }

  // 删除固件版本
  static async deleteFirmware(firmwareId: number) {
    try {
      logger.info("🗑️ [真实接口] 删除固件版本", firmwareId);
      const data = await apiClient.delete(
        `${config.apiUrls.OTA}/firmware/${firmwareId}`
      );
      return data;
    } catch (error) {
      logger.error("删除固件版本失败:", error);
      return createEmptyResponse();
    }
  }
}

// === 租户与API密钥相关API封装 ===
export class TenantServiceApi {
  static async listTenants(
    params: {
      page?: number;
      page_size?: number;
      name?: string;
      subname?: string;
      is_active?: boolean;
    } = {}
  ) {
    try {
      const data = await apiClient.get(config.apiUrls.TENANTS, { params });
      return data;
    } catch (error) {
      logger.error("获取租户列表失败:", error);
      return { data: { code: 500, data: { total: 0, items: [] } } } as any;
    }
  }

  static async createTenant(payload: Record<string, unknown>) {
    try {
      const data = await apiClient.post(config.apiUrls.TENANTS, payload);
      return data;
    } catch (error) {
      logger.error("创建租户失败:", error);
      return { data: { code: 500 } } as any;
    }
  }

  static async updateTenant(
    tenantId: number | string,
    payload: Record<string, unknown>
  ) {
    try {
      const data = await apiClient.put(
        config.apiUrls.TENANT_BY_ID(tenantId),
        payload
      );
      return data;
    } catch (error) {
      logger.error("更新租户失败:", error);
      return { data: { code: 500 } } as any;
    }
  }

  static async deleteTenant(tenantId: number | string) {
    try {
      const data = await apiClient.delete(
        config.apiUrls.TENANT_BY_ID(tenantId)
      );
      return data;
    } catch (error) {
      logger.error("删除租户失败:", error);
      return { data: { code: 500 } } as any;
    }
  }

  static async listApiKeys(tenantId: number | string) {
    try {
      const data = await apiClient.get(config.apiUrls.TENANT_APIKEYS(tenantId));
      return data;
    } catch (error) {
      logger.error("获取API密钥列表失败:", error);
      return { data: { code: 500, data: [] } } as any;
    }
  }

  static async createApiKey(
    tenantId: number | string,
    payload: { name: string; description?: string }
  ) {
    try {
      const data = await apiClient.post(
        config.apiUrls.TENANT_APIKEY_CREATE(tenantId),
        payload
      );
      return data;
    } catch (error) {
      logger.error("创建API密钥失败:", error);
      return { data: { code: 500 } } as any;
    }
  }

  static async deleteApiKey(tenantId: number | string, keyId: number | string) {
    try {
      const data = await apiClient.delete(
        config.apiUrls.TENANT_APIKEY_DELETE(tenantId, keyId)
      );
      return data;
    } catch (error) {
      logger.error("删除API密钥失败:", error);
      return { data: { code: 500 } } as any;
    }
  }

  static async updateApiStatus(
    tenantId: number | string,
    apiKeyId: number | string,
    params: { enabled?: boolean; name?: string; description?: string }
  ) {
    try {
      const url = config.apiUrls.TENANT_APIKEY_STATUS(tenantId, apiKeyId);
      const data = await apiClient.put(url, {}, { params });
      return data;
    } catch (error) {
      logger.error("更新API设置失败:", error);
      return { data: { code: 500 } } as any;
    }
  }
}

// === 用户与个人资料相关API封装 ===
export class UserServiceApi {
  // 获取用户列表（按权限后端已过滤）
  static async listUsers(params: { skip?: number; limit?: number } = {}) {
    try {
      const url = config.apiUrls ? (config.apiUrls as any)["USERS"] : undefined;
      const requestUrl = url || `${config.apiBaseUrl}${config.apiPaths.USERS}`;
      const data = await apiClient.get(requestUrl, { params });
      return data;
    } catch (error) {
      logger.error("获取用户列表失败:", error);
      return { data: { code: 500, data: { total: 0, items: [] } } } as any;
    }
  }

  // 创建用户（admin 仅能创建 operator；superadmin 任意）
  static async createUser(payload: {
    username: string;
    password: string;
    display_name?: string;
    role: string;
    is_active?: boolean;
    tenant_id?: number | string;
  }) {
    try {
      const requestUrl = `${config.apiBaseUrl}${config.apiPaths.USERS}`;
      const data = await apiClient.post(requestUrl, payload);
      return data;
    } catch (error) {
      logger.error("创建用户失败:", error);
      return { data: { code: 500 } } as any;
    }
  }

  // 删除用户（admin 仅能删除 operator；superadmin 任意）
  static async deleteUser(userId: number) {
    try {
      const requestUrl = `${config.apiBaseUrl}${config.apiPaths.USER_BY_ID(
        userId
      )}`;
      const data = await apiClient.delete(requestUrl);
      return data;
    } catch (error) {
      logger.error("删除用户失败:", error);
      return { data: { code: 500 } } as any;
    }
  }

  // 修改本人显示名
  static async updateMyProfile(display_name: string) {
    try {
      const requestUrl = `${config.apiBaseUrl}${config.apiPaths.USER_ME_PROFILE}`;
      const data = await apiClient.patch(requestUrl, { display_name });
      return data;
    } catch (error) {
      logger.error("更新个人资料失败:", error);
      return { data: { code: 500 } } as any;
    }
  }

  // 修改本人密码
  static async updateMyPassword(old_password: string, new_password: string) {
    try {
      const requestUrl = `${config.apiBaseUrl}${config.apiPaths.USER_ME_PASSWORD}`;
      const data = await apiClient.patch(requestUrl, {
        old_password,
        new_password,
      });
      return data;
    } catch (error) {
      logger.error("修改密码失败:", error);
      return { data: { code: 500 } } as any;
    }
  }
}
